import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, X, Smartphone } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const DISMISS_KEY = 'wingdrive_pwa_prompt_dismissed'

function isStandaloneMode(): boolean {
  const mediaStandalone = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = (window.navigator as any).standalone === true
  return mediaStandalone || iosStandalone
}

function isIosDevice(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
}

export function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(false)
  const [installing, setInstalling] = useState(false)

  const shouldShowIosHint = useMemo(() => isIosDevice() && !isStandaloneMode(), [])

  useEffect(() => {
    if (isStandaloneMode()) return

    const dismissed = localStorage.getItem(DISMISS_KEY) === '1'
    if (dismissed) return

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
      setVisible(true)
    }

    const onInstalled = () => {
      setVisible(false)
      setDeferredPrompt(null)
      localStorage.removeItem(DISMISS_KEY)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)

    if (shouldShowIosHint) {
      const timer = window.setTimeout(() => setVisible(true), 2000)
      return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.removeEventListener('appinstalled', onInstalled)
        window.clearTimeout(timer)
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [shouldShowIosHint])

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setVisible(false)
  }

  const handleInstall = async () => {
    if (!deferredPrompt) return
    setInstalling(true)
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome !== 'accepted') {
        localStorage.setItem(DISMISS_KEY, '1')
      }
      setVisible(false)
      setDeferredPrompt(null)
    } finally {
      setInstalling(false)
    }
  }

  const canInstallDirectly = !!deferredPrompt
  if (!visible || isStandaloneMode()) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(2, 6, 23, 0.55)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          style={{
            width: '100%',
            maxWidth: 420,
            background: 'linear-gradient(135deg, #020617 0%, #1e3a8a 70%, #2563eb 100%)',
            border: '1px solid rgba(96, 165, 250, 0.55)',
            borderRadius: 16,
            boxShadow: '0 18px 42px rgba(0, 0, 0, 0.45)',
            color: '#e2e8f0',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(59, 130, 246, 0.25)',
                  border: '1px solid rgba(147, 197, 253, 0.5)',
                }}
              >
                <Smartphone size={18} color="#bfdbfe" />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>Instala WingDrive</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#cbd5e1' }}>
                  Acceso rapido, mejor rendimiento y experiencia tipo app.
                </p>
              </div>
            </div>
            <button onClick={handleDismiss} aria-label="Cerrar">
              <X size={18} color="#cbd5e1" />
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            {canInstallDirectly ? (
              <button
                onClick={handleInstall}
                disabled={installing}
                style={{
                  width: '100%',
                  background: '#fbbf24',
                  color: '#111827',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <Download size={16} />
                {installing ? 'Instalando...' : 'Instalar aplicacion'}
              </button>
            ) : (
              <div
                style={{
                  background: 'rgba(15, 23, 42, 0.7)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  borderRadius: 10,
                  padding: 12,
                  fontSize: 13,
                  color: '#dbeafe',
                  lineHeight: 1.4,
                }}
              >
                En iPhone: abre compartir y toca "Agregar a pantalla de inicio".
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

